import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { OdataInputFieldPropertiesService } from '../../../services/input-field-properties/odata-input-field-properties.service';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    initInputFieldProperties,
    inputFieldPropertiesFailed,
    inputFieldPropertiesLoaded,
    inputFieldPropertiesLoadedFromStorage,
} from '../actions/input-field-properties.actions';
import {
    INPUT_FIELD_PROPERTIES_FEATURE_KEY,
    InputFieldPropertyState,
} from '../input-field-properties-collection.state';
import { selectInputFieldPropertiesLoaded } from '../selectors/input-field-properties.selector';

/**
 * Owns the input-field-properties load flow and the persistence mirror. `loadInputFieldProperties$`
 * fetches the reference data from OData on every `initInputFieldProperties` dispatch (initial boot
 * and after sync); `persistInputFieldProperties$` caches the slice after each backend load;
 * `hydrateInputFieldPropertiesOnInit$` reads the cache back at startup so the properties are
 * available offline while the network request is in flight. Both hydrate steps are guarded on
 * {@link selectInputFieldPropertiesLoaded} so a slower cache read can never overwrite fresher
 * backend data.
 */
@Injectable()
export class InputFieldPropertiesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly odataInputFieldPropertiesService = inject(OdataInputFieldPropertiesService);

    /** Fetch input field properties from the backend and populate the slice. */
    public loadInputFieldProperties$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initInputFieldProperties),
            switchMap(() =>
                this.odataInputFieldPropertiesService.getInputFieldPropertiesState().pipe(
                    map((properties) => inputFieldPropertiesLoaded({ properties })),
                    catchError(() => of(inputFieldPropertiesFailed())),
                ),
            ),
        ),
    );

    /** Cache the slice whenever the backend load populates it. */
    public persistInputFieldProperties$ = createEffect(() =>
        this.actions$.pipe(
            ofType(inputFieldPropertiesLoaded),
            map(() => persistSlice({ featureKey: INPUT_FIELD_PROPERTIES_FEATURE_KEY })),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateInputFieldPropertiesOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initInputFieldProperties),
            concatLatestFrom(() => this.store.select(selectInputFieldPropertiesLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: INPUT_FIELD_PROPERTIES_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the slice, unless the backend already won. */
    public applyHydratedInputFieldProperties$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === INPUT_FIELD_PROPERTIES_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectInputFieldPropertiesLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                inputFieldPropertiesLoadedFromStorage({
                    properties: (data as InputFieldPropertyState[] | undefined) ?? [],
                }),
            ),
        ),
    );
}
